import { useEffect, useState } from "react";
import "./Analytics"; // Import your custom styles
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import DashboardCard from "../job-cards/Components/dashboard-card/DashboardCard";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

interface IUser {
  email: string;
  createdAt: string;
  lastLoginAt: string;
}

const Analytics = () => {
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(
      "https://us-central1-oranjemund-town-council-38942.cloudfunctions.net/fetchUserAccounts"
    )
      .then((res) => res.json())
      .then((data) => {
        setUsers(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Helper: parse date string safely
  const parseDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  // Stats
  const totalUsers = users.length;
  const now = new Date();
  const activeThreshold = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago

  const activeUsers = users.filter((u) => {
    const lastLogin = parseDate(u.lastLoginAt);
    return lastLogin && lastLogin >= activeThreshold;
  }).length;

  const inactiveUsers = totalUsers - activeUsers;

  // Recent 5 logins sorted desc by lastLoginAt date
  const recentLogins = [...users]
    .filter((u) => parseDate(u.lastLoginAt))
    .sort(
      (a, b) =>
        parseDate(b.lastLoginAt)!.getTime() -
        parseDate(a.lastLoginAt)!.getTime()
    )
    .slice(0, 5);

  const createUserTable = (userList) => ({
    table: {
      headerRows: 1,
      widths: ["*", "*", "*"],
      body: [
        [
          { text: "Email", style: "tableHeader" },
          { text: "Created At", style: "tableHeader" },
          { text: "Last Login", style: "tableHeader" },
        ],
        ...userList.map((u) => [
          { text: u.email || "N/A", style: "tableContent" },
          { text: u.createdAt || "N/A", style: "tableContent" },
          { text: u.lastLoginAt || "N/A", style: "tableContent" },
        ]),
      ],
    },
    layout: {
      fillColor: (rowIndex: number) => {
        return rowIndex === 0
          ? "#004c98"
          : rowIndex % 2 === 0
          ? "#f9f9f9"
          : null;
      },
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => "#cccccc",
      vLineColor: () => "#cccccc",
    },
  });

  const exportToPDF = () => {
    const totalUsers = users.length;
    const now = new Date();
    const activeThreshold = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // Last 30 days

    const activeUsers = users.filter((u) => {
      const lastLogin = parseDate(u.lastLoginAt);
      return lastLogin && lastLogin >= activeThreshold;
    });

    const inactiveUsers = users.filter((u) => {
      const lastLogin = parseDate(u.lastLoginAt);
      return !lastLogin || lastLogin < activeThreshold;
    });

    const docDefinition: any = {
      content: [
        { text: "User Account Report", style: "header" },
        {
          columns: [
            { text: `Total Users: ${totalUsers}`, style: "stats" },
            {
              text: `Active Users (last 30 days): ${activeUsers.length}`,
              style: "stats",
            },
            { text: `Inactive Users: ${inactiveUsers.length}`, style: "stats" },
          ],
          margin: [0, 0, 0, 10],
        },

        { text: "Active Users", style: "subheader" },
        createUserTable(activeUsers),

        { text: "Inactive Users", style: "subheader", margin: [0, 10, 0, 0] },
        createUserTable(inactiveUsers),

        {
          text: `Generated: ${new Date().toLocaleString()}`,
          margin: [0, 15, 0, 0],
          fontSize: 8,
        },
      ],
      styles: {
        header: { fontSize: 16, bold: true, margin: [0, 0, 0, 10] },
        subheader: { fontSize: 12, bold: true, margin: [0, 10, 0, 5] },
        stats: { fontSize: 10, bold: true },
        tableHeader: {
          bold: true,
          fillColor: "#004c98",
          color: "white",
          fontSize: 10,
          alignment: "left",
        },
        tableContent: { fontSize: 9, alignment: "left" },
      },
    };

    pdfMake.createPdf(docDefinition).download("User_Report.pdf");
  };

  // const exportToPDF = () => {
  //   const docDefinition: any = {
  //     content: [
  //       { text: "User Account Report", style: "header" },
  //       {
  //         columns: [
  //           { text: `Total Users: ${totalUsers}`, style: "subheader" },
  //           { text: `Active (30 days): ${activeUsers}`, style: "subheader" },
  //           { text: `Inactive: ${inactiveUsers}`, style: "subheader" },
  //         ],
  //         margin: [0, 0, 0, 15],
  //       },
  //       {
  //         text: "Recent Logins",
  //         style: "subheader",
  //         margin: [0, 0, 0, 5],
  //       },
  //       {
  //         table: {
  //           headerRows: 1,
  //           widths: ["*", "*", "*"],
  //           body: [
  //             [
  //               { text: "Email", style: "tableHeader" },
  //               { text: "Created At", style: "tableHeader" },
  //               { text: "Last Login", style: "tableHeader" },
  //             ],
  //             ...recentLogins.map((u) => [
  //               { text: u.email || "N/A", style: "tableContent" },
  //               { text: u.createdAt || "N/A", style: "tableContent" },
  //               { text: u.lastLoginAt || "N/A", style: "tableContent" },
  //             ]),
  //           ],
  //         },
  //         layout: "lightHorizontalLines",
  //         margin: [0, 0, 0, 15],
  //       },
  //       {
  //         text: "All Users",
  //         style: "subheader",
  //         margin: [0, 0, 0, 5],
  //       },
  //       {
  //         table: {
  //           headerRows: 1,
  //           widths: ["*", "*", "*"],
  //           body: [
  //             [
  //               { text: "Email", style: "tableHeader" },
  //               { text: "Created At", style: "tableHeader" },
  //               { text: "Last Login", style: "tableHeader" },
  //             ],
  //             ...users.map((u) => [
  //               { text: u.email || "N/A", style: "tableContent" },
  //               { text: u.createdAt || "N/A", style: "tableContent" },
  //               { text: u.lastLoginAt || "N/A", style: "tableContent" },
  //             ]),
  //           ],
  //         },
  //         layout: "lightHorizontalLines",
  //       },
  //       {
  //         text: `Generated: ${new Date().toLocaleString()}`,
  //         margin: [0, 15, 0, 0],
  //         fontSize: 8,
  //         alignment: "right",
  //       },
  //     ],
  //     styles: {
  //       header: { fontSize: 18, bold: true, margin: [0, 0, 0, 15] },
  //       subheader: { fontSize: 12, bold: true, margin: [0, 10, 0, 5] },
  //       tableHeader: {
  //         bold: true,
  //         fillColor: "#004c98",
  //         color: "white",
  //         fontSize: 10,
  //         alignment: "left",
  //       },
  //       tableContent: { fontSize: 9, alignment: "left" },
  //     },
  //   };

  //   pdfMake.createPdf(docDefinition).download("User_Report.pdf");
  // };

  return (
    <div className="user-dashboard">
      <h2 className="dashboard-title">User Account Dashboard</h2>

      {/* Stats Cards */}
      <div
        className="uk-grid-small uk-grid-match uk-child-width-expand@s uk-child-width-expand@m uk-margin"
        data-uk-grid
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          paddingRight: "20px",
          paddingLeft: "20px",
        }}
      >
        <DashboardCard
          variant="primary"
          icon="👥"
          label="Total Users"
          value={totalUsers}
          tooltip="Total users in the system"
        />
        <DashboardCard
          variant="success"
          icon="✅"
          label="Active Users (30 days)"
          value={activeUsers}
          tooltip="Users active in the last 30 days"
        />
        <DashboardCard
          variant="danger"
          icon="❌"
          label="Inactive Users"
          value={inactiveUsers}
          tooltip="Users inactive in the last 30 days"
        />
      </div>

      {/* Recent Logins Table */}
      <div className="uk-container-expand">
        <h3 className="section-title">Recent Logins</h3>
        {recentLogins.length === 0 ? (
          <p className="no-data">No recent logins found.</p>
        ) : (
          <table className="uk-table uk-table-divider uk-table-hover uk-table-striped uk-table-middle uk-table-responsive">
            <thead>
              <tr>
                <th>Email</th>
                <th>Created At</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {recentLogins.map((u, i) => (
                <tr key={i}>
                  <td>{u.email}</td>
                  <td>{u.createdAt}</td>
                  <td>{u.lastLoginAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Full Users Table */}
      <div className="uk-container-expand uk-margin-large-top">
        <h3 className="section-title">All Users</h3>
        {loading ? (
          <p className="no-data">Loading...</p>
        ) : (
          <table className="uk-table uk-table-divider uk-table-hover uk-table-striped uk-table-middle uk-table-responsive">
            <thead>
              <tr>
                <th>Email</th>
                <th>Created At</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={i}>
                  <td>{u.email}</td>
                  <td>{u.createdAt}</td>
                  <td>{u.lastLoginAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Export Button */}
      <div className="export-btn-container">
        <button className="export-btn" onClick={exportToPDF}>
          Export to PDF
        </button>
      </div>
    </div>
  );
};

export default Analytics;
